import { DecimalPipe } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { delay, filter, map, retryWhen, scan, take, tap } from 'rxjs/operators';
import { ConditionalNotificationConstants } from '@easy-lib/constants';
import { SharedRoutes } from '@easy-lib/constants';
import { AddOrUpdateResponse } from '@easy-lib/modules/shared/models';
import {
	AddStockConditionItemDto,
	BulkAddStockConditionItemsDto,
	ConditionOperatorType,
	Field,
	FieldType,
	IFieldValue,
	IStockState,
	SimpleCondition,
	StockCondition,
	StockConditionRequest,
	StockConditionSet,
	SymbolCondition,
	UserStockConditionSets
} from '../models/condition.model';
import { ILsData, LsSchemaType } from '../../shared/models/ls.model';
import { LsSchemas } from '../../shared/models/lsSchemas';
import { pSortGeneric, pSortGenericByOrderType } from '@easy-lib/tools';
import { ConditionalRiskAgreement, StockConditionSetWithSymbol } from '../models/condition.model';
import { AccountService } from '@easy-lib/modules/account-activity';
import { DeviceTypeEnum } from '@easy-lib/modules/account-activity';
import { BaseService, SymbolService } from '@easy-lib/modules/shared/services';
import { guid } from '@easy-lib/tools';
import {
	LsService,
	MarketDataService,
	NotificationService,
	NotificationType,
	UserDeviceService
} from '../../shared/services';
import { TimeService } from '@easy-lib/modules/shared/services';

@Injectable({
	providedIn: 'root'
})
export class ConditionalNotificationService extends BaseService {
	public lsService: LsService = inject(LsService);
	private marketDataService: MarketDataService = inject(MarketDataService);
	private notificationService: NotificationService = inject(NotificationService);
	private decimalPipe: DecimalPipe = inject(DecimalPipe);
	private accountService: AccountService = inject(AccountService);
	private symbolService: SymbolService = inject(SymbolService);
	private userDeviceService: UserDeviceService = inject(UserDeviceService);
	private timeService: TimeService = inject(TimeService);

	private stockConditionSetSub = new BehaviorSubject<UserStockConditionSets>(undefined);
	public stockConditionSetObs$ = this.stockConditionSetSub.asObservable().pipe(filter(Boolean));

	private stockConditionSetCountSub = new BehaviorSubject<Map<string, SymbolCondition>>(undefined);
	public stockConditionSetCountObs$ = this.stockConditionSetCountSub.asObservable().pipe(filter(Boolean));

	userStockConditionSets: UserStockConditionSets;
	stockStates: { [stockIsin: string]: IStockState } = {};
	stockSubs: { [stockIsin: string]: Subscription } = {};
	public stockIsinMapper: {
		[isin: string]: { symbol: string; title: string };
	} = {};
	private notifSubject: Subject<Date> = new Subject<Date>();
	notif$ = this.notifSubject.asObservable();

	// Http paths
	private get crudUrl(): string {
		return this.mtsPath + this.apiUrls.easy + 'StockCondition';
	}

	private get bulkAddStockConditionItemsDtoUrl(): string {
		return this.mtsPath + this.apiUrls.easy + 'StockCondition/BulkAdd';
	}

	private get conditionalRiskAgreementUrl(): string {
		return this.mtsPath + this.apiUrls.easy + 'StockCondition/riskAgreement';
	}

	private readonly notifiedConditionIdsStorageKey: string = 'notifiedConditionIds';
	private readonly notifiedConditionDateKey: string = 'notifiedConditionDate';

	get stockConditions(): StockCondition[] {
		if (
			!this.userStockConditionSets ||
			!this.userStockConditionSets.stockConditionSets ||
			!this.userStockConditionSets.stockConditionSets.length
		)
			return [];
		const _stockConditions = this.userStockConditionSets.stockConditionSets
			.map(scs => scs.stockConditionItems)
			.reduce((a, b) => a.concat(b));
		return _stockConditions;
	}

	get unseenNotifiedConditionsCount(): number {
		return this.stockConditions.filter(s => this.isConditionNotified(s) && !s.notifiedConditionIsSeen).length;
	}

	get pendingConditions(): StockCondition[] {
		return this.stockConditions.filter(s => !this.isConditionNotified(s));
	}

	get pendingIsinsSet(): Set<string> {
		return new Set<string>(this.pendingConditions.map(c => c.stockIsin));
	}

	get pendingIsinsArray(): string[] {
		return Array.from(this.pendingIsinsSet);
	}

	// get mfdonlineUserName(): string {
	//   return this.accountService.userAccountInfo?.customerIsin;
	// }

	get stockConditionsStorageKey(): string {
		if (!this.accountService?.userAccountInfo?.value?.customerIsin) return null;
		return 'stockConditions-' + this.accountService?.userAccountInfo?.value?.customerIsin;
	}

	private _riskAgreementIsAccepted = false;

	get riskAgreementIsAccepted() {
		return this._riskAgreementIsAccepted;
	}

	set riskAgreementIsAccepted(value) {
		this._riskAgreementIsAccepted = value;
		this.conditionalRiskAgreementSubject.next(value);
	}

	get conditionsSynched(): boolean {
		// todo: موقت تا زمانی که شروط اکثر کاربران در سمت سرور ذخیره شود
		const json = localStorage.getItem(this.stockConditionsStorageKey);
		if (json) return false;
		return true;
	}

	get notifiedConditionIds(): string[] {
		const json = localStorage.getItem(this.notifiedConditionIdsStorageKey);
		if (json) {
			const ids: string[] = JSON.parse(json);
			return ids;
		}
		return [];
	}

	newConditionSubject = new BehaviorSubject<number>(0);
	newConditionObs$ = this.newConditionSubject.asObservable();

	conditionsSyncSubject = new Subject<boolean>();
	conditionsSync$ = this.conditionsSyncSubject.asObservable();

	conditionsAreSetSubject = new Subject<boolean>();
	conditionsAreSet$ = this.conditionsAreSetSubject.asObservable();

	conditionalRiskAgreementSubject = new BehaviorSubject<boolean>(false);
	conditionalRiskAgreement$ = this.conditionalRiskAgreementSubject.asObservable();

	showConditionalRiskAgreementSubject = new Subject<boolean>();
	showConditionalRiskAgreement$ = this.showConditionalRiskAgreementSubject.asObservable();

	showAnalyticsRiskModalSubject = new BehaviorSubject<boolean>(!localStorage.getItem('analyticsRiskModal'));

	constructor() {
		super();
		this.updateNotificationState();
		this.MapStockIsinsToNames();

		this.stockConditionSetObs$.subscribe((value: UserStockConditionSets) => {
			const map = new Map<string, SymbolCondition>(
				value.stockConditionSets.map(item => [
					item.isin,
					{
						count: item.stockConditionItems.length,
						class: this.getStockConditionFigureClass(item.isin),
						notifiedCount: item.stockConditionItems.filter(
							s => this.isConditionNotified(s) && !s.notifiedConditionIsSeen
						).length
					}
				])
			);
			this.stockConditionSetCountSub.next(map);
		});
	}

	playAudio() {
		try {
			const audio = new Audio();
			// audio.src = "./assets/audio/notify.wav";
			audio.src = '../assets/audio/notify.wav';
			audio.load();
			audio.play().catch(null);
		} catch (e) {
			null;
		}
	}

	updateNotificationState() {
		const lastDate = localStorage.getItem(this.notifiedConditionDateKey);
		if (!lastDate) {
			this.updateNotifiedConditionDate();
			return;
		}
		if (this.currentDateNumber() - +lastDate > 0) {
			this.removeNotifiedconditionalIds();
			this.updateNotifiedConditionDate();
		}
	}

	currentDateNumber = () => new Date().setHours(0, 0, 0, 0);
	updateNotifiedConditionDate = () =>
		localStorage.setItem(this.notifiedConditionDateKey, this.currentDateNumber().toString());
	removeNotifiedconditionalIds = () => localStorage.removeItem(this.notifiedConditionIdsStorageKey);

	async MapStockIsinsToNames() {
		const symbols = await this.symbolService.getSymbols();
		if (symbols) {
			for (let i = 0; i < symbols.length; i++) {
				const symbol = symbols[i];
				this.stockIsinMapper[symbol.symbolIsin] = {
					symbol: symbol.symbolName,
					title: symbol.title
				};
			}
		}
	}

	initStockConditions() {
		// is called once at program startup
		if (!this.conditionsSynched) {
			this.riskAgreementIsAccepted = true;
			this.checkAllConditinosWhenSet();
			this.moveLocalConditionsToServer();
		} else {
			this.getStockConditions()
				.pipe(
					tap(userStockConditionSets => {
						if (userStockConditionSets?.stockConditionSets?.length) {
							this.riskAgreementIsAccepted = true;
							this.checkAllConditinosWhenSet();
							this.setUserStockConditionSets(userStockConditionSets);
						} else {
							this.riskAgreementIsAccepted = false;
							this.httpService
								.get<ConditionalRiskAgreement>(this.conditionalRiskAgreementUrl)
								.pipe(take(1))
								.pipe(
									tap(agreement => {
										if (agreement) this.riskAgreementIsAccepted = true;
										const emptyUserStockConditionSets = {
											id: null,
											creationDateTime: null,
											stockConditionSets: []
										};
										this.setUserStockConditionSets(emptyUserStockConditionSets);
									})
								)
								.subscribe();
						}
					})
				)
				.subscribe();
		}
	}

	private checkAllConditinosWhenSet() {
		this.conditionsAreSet$.pipe(take(1)).subscribe(res => {
			if (res) {
				if (this.unseenNotifiedConditionsCount > 0) this.emitUnseenNotifiedConditionsCount();
				this.initStatesAndCheckConditionsForAll(this.pendingIsinsArray);
			}
		});
	}

	private getStockConditions(): Observable<UserStockConditionSets> {
		return this.httpService.get(this.crudUrl).pipe(
			map((result: any[]) =>
				result.map(t => ({
					...t,
					simpleConditions:
						t.rules?.map(x => ({
							fieldValue: { field: x.field, value: x.value },
							operator: x.operator
						})) ?? []
				}))
			),
			// map((result: any[]) => result.map(t => ({ ...t, simpleConditions: t.rules ?? [] }))),
			map((result: any[]) =>
				result.reduce(function (r, a) {
					r[a.symbolIsin] = r[a.symbolIsin] || [];
					r[a.symbolIsin].push(a);
					return r;
				}, Object.create(null))
			),
			map((result: any[]) => {
				const conditionSet = [];
				for (const isin in result) {
					conditionSet.push({ isin: isin, stockConditionItems: result[isin] });
				}
				return conditionSet;
			}),
			map(
				(result: any[]) =>
					({
						id: guid(),
						creationDateTime: new Date(),
						stockConditionSets: result
					}) as UserStockConditionSets
			)
		);
	}

	private setUserStockConditionSets(userStockConditionSets: UserStockConditionSets) {
		if (!userStockConditionSets || !userStockConditionSets.stockConditionSets) return;
		this.userStockConditionSets = {
			id: userStockConditionSets.id,
			creationDateTime: userStockConditionSets.creationDateTime,
			stockConditionSets: []
		};

		for (const stockConditionSet of userStockConditionSets.stockConditionSets) {
			const nextStockConditionItems = stockConditionSet.stockConditionItems;
			if (!nextStockConditionItems || !nextStockConditionItems.length) continue;
			const isin = stockConditionSet.isin;

			const nextStockConditionSet = this.makeStockConditionSet(isin, nextStockConditionItems);
			this.userStockConditionSets.stockConditionSets.push(nextStockConditionSet);
		}
		this.conditionsAreSetSubject.next(true);
		this.stockConditionSetSub.next(this.userStockConditionSets);
	}

	private makeStockConditionSet(isin: string, stockConditionItems: StockCondition[]): StockConditionSet {
		const stockConditionSet: StockConditionSet = {
			isin: isin,
			stockConditionItems: []
		};
		for (const item of stockConditionItems) {
			const nextStockCondition = new StockCondition(isin, item.id, item.name, item.notifiedConditionIsSeen);
			nextStockCondition.creationDateTime = item.creationDateTime;

			item.simpleConditions.forEach(simpleCond => {
				const nextSimpleCondition = new SimpleCondition(simpleCond.fieldValue, simpleCond.operator);
				nextStockCondition.simpleConditions.push(nextSimpleCondition);
			});
			stockConditionSet.stockConditionItems.push(nextStockCondition);
		}
		return stockConditionSet;
	}

	private emitUnseenNotifiedConditionsCount() {
		this.newConditionSubject.next(this.unseenNotifiedConditionsCount);
	}

	acceptRiskAgreement() {
		this.httpService
			.post<any>(this.conditionalRiskAgreementUrl, { body: { dummy: true } })
			.pipe(take(1))
			.pipe(
				tap(result => {
					if (result) this.riskAgreementIsAccepted = true;
				})
			)
			.subscribe();
	}

	clearLocalStorage() {
		if (!this.stockConditionsStorageKey) return;
		localStorage.removeItem(this.stockConditionsStorageKey);
	}

	getStockCondition(id: string): StockCondition {
		const idx = this.stockConditions.findIndex(x => x.id === id);
		if (idx < 0) return null;
		return this.stockConditions[idx];
	}

	moveLocalConditionsToServer(withNotify = false) {
		const json = localStorage.getItem(this.stockConditionsStorageKey); // todo: local storage is used only in old version and should be discarded after moving all object models to server-side
		if (json) {
			const localStockConditions = [];
			const savedStockConditions: StockCondition[] = JSON.parse(json);
			savedStockConditions.forEach(cond => {
				const nextStockCondition = new StockCondition(
					cond.stockIsin,
					cond.id,
					cond.name,
					cond.notifiedConditionIsSeen
				);

				cond.simpleConditions.forEach(simpleCond => {
					const nextSimpleCondition = new SimpleCondition(simpleCond.fieldValue, simpleCond.operator);
					nextStockCondition.simpleConditions.push(nextSimpleCondition);
				});
				localStockConditions.push(nextStockCondition);
			});

			const bulkAddStockConditionItemsDto: BulkAddStockConditionItemsDto = {
				addStockConditionItemDtos: []
			};
			for (const stockCondition of localStockConditions) {
				stockCondition.id = guid();
				const addStockConditionItemDto: AddStockConditionItemDto = {
					stockCondition: stockCondition
				};
				bulkAddStockConditionItemsDto.addStockConditionItemDtos.push(addStockConditionItemDto);
			}
			this.httpService
				.post<any>(this.bulkAddStockConditionItemsDtoUrl, { body: bulkAddStockConditionItemsDto })
				.pipe(take(1))
				.pipe(
					tap(
						userStockConditionSets => {
							if (withNotify)
								this.notificationService.alert(
									'همگام‌سازی هشدارها با موفقیت انجام شد',
									NotificationType.success
								);
							this.setUserStockConditionSets(userStockConditionSets);
							this.conditionsSyncSubject.next(true);
							this.clearLocalStorage();
						},
						() => {
							this.notificationService.alert('خطا در همگام‌سازی هشدارها', NotificationType.danger);
							this.conditionsSyncSubject.next(false);
						}
					)
				)
				.subscribe();
		}
	} // todo: this is only for migration state and will be removed in next versions

	makeStockConditionRequest(stockCondition: StockCondition): StockConditionRequest {
		const request = {
			...stockCondition,
			rules: stockCondition.simpleConditions.map(t => ({
				field: t.fieldValue.field,
				value: t.fieldValue.value,
				operator: t.operator
			}))
		};
		delete request?.simpleConditions;

		return request as StockConditionRequest;
	}

	addStockCondition(stockCondition: StockCondition) {
		// stockCondition.id = guid();
		// const addStockConditionItemDto: AddStockConditionItemDto = { stockCondition: stockCondition };
		const request = this.makeStockConditionRequest(stockCondition);
		this.httpService
			.post<AddOrUpdateResponse>(this.crudUrl, { body: request })
			.pipe(take(1))
			.pipe(
				tap({
					next: res => {
						stockCondition.id = res.id;
						this.addNewStockConditionsToLocal(stockCondition);
						this.notificationService.alert('هشدار شرطی جدید ساخته شد', NotificationType.success);
						this.subscribeLsIfNeededAndCheckConditions(stockCondition?.symbolIsin);
					},
					error: () => {
						this.notificationService.alert('خطا در ساخت هشدار شرطی', NotificationType.danger);
					}
				})
			)
			.subscribe();
	}

	private addNewStockConditionsToLocal(stockCondition: StockCondition) {
		if (!this.userStockConditionSets?.stockConditionSets) {
			this.initStockConditions();
			return;
		}

		let stockConditionSet = this.userStockConditionSets?.stockConditionSets?.filter(
			x => x.isin == stockCondition.symbolIsin
		)[0];
		if (stockConditionSet && stockConditionSet?.stockConditionItems) {
			stockConditionSet.stockConditionItems.push(stockCondition);
			return;
		}

		stockConditionSet = {
			isin: stockCondition.symbolIsin,
			stockConditionItems: [stockCondition]
		};
		this.userStockConditionSets.stockConditionSets.push(stockConditionSet);
		this.stockConditionSetSub.next(this.userStockConditionSets);
	}

	private updateStockConditionsInLocal(stockCondition: StockCondition, idx: number) {
		const stockConditionSetIndex = this.userStockConditionSets?.stockConditionSets?.findIndex(
			x => x.isin == stockCondition.symbolIsin
		);

		if (stockConditionSetIndex <= -1) {
			this.initStockConditions();
			return;
		}

		this.userStockConditionSets.stockConditionSets[stockConditionSetIndex].stockConditionItems[idx] =
			stockCondition;
		this.stockConditionSetSub.next(this.userStockConditionSets);
	}

	removeStockCondition(id: string) {
		const idx = this.stockConditions.findIndex(x => x.id === id);
		if (idx < 0) return;

		const isin = this.stockConditions[idx].stockIsin;
		const deleteUrl = `${this.crudUrl}/${id}`;

		this.httpService
			.delete(deleteUrl, null)
			.pipe(take(1))
			.pipe(
				tap({
					next: () => {
						this.notificationService.alert('هشدار شرطی حذف شد', NotificationType.success);
						if (this.isConditionNotified(this.stockConditions[idx])) this.removeNotifiedConditionId(id);

						this.removestockconditionFromLocal(isin, id);
						//  this.setUserStockConditionSets(newUserStockConditionSets);
						this.unsubscribeStockIfNeeded(isin);
						this.emitUnseenNotifiedConditionsCount();
					},
					error: () => {
						this.notificationService.alert('خطا در حذف هشدار شرطی', NotificationType.danger);
					}
				})
			)
			.subscribe();
	}

	removestockconditionFromLocal(isin: string, id: string) {
		const removedIndex = this.userStockConditionSets.stockConditionSets.findIndex(t => t.isin === isin);
		const data = this.userStockConditionSets.stockConditionSets.find(t => t.isin === isin);
		data.stockConditionItems = data.stockConditionItems.filter(t => t.id !== id);
		if (data.stockConditionItems.length === 0) {
			this.userStockConditionSets.stockConditionSets.splice(removedIndex, 1);
		}
		this.stockConditionSetSub.next(this.userStockConditionSets);
	}

	updateStockCondition(stockCondition: StockCondition, updateBySystem = false) {
		const idx = this.userStockConditionSets.stockConditionSets
			?.find(x => x.isin === stockCondition.stockIsin)
			?.stockConditionItems?.findIndex(x => x.id === stockCondition.id);
		if (idx < 0) return;
		const isin = stockCondition.stockIsin;
		const request = this.makeStockConditionRequest(stockCondition);
		this.httpService
			.post<StockConditionSet>(this.crudUrl, { body: request })
			.pipe(take(1))
			.pipe(
				tap(() => {
					if (!updateBySystem) {
						this.updateStockConditionsInLocal(stockCondition, idx);
						this.removeNotifiedConditionId(stockCondition.id);
						this.subscribeLsIfNeededAndCheckConditions(isin);
					}
				})
			)
			.subscribe();
	}

	private subscribeLsIfNeededAndCheckConditions(isin: string) {
		const existingSub = this.stockSubs[isin];
		if (!existingSub) this.initStatesAndCheckConditions(isin);
		else this.checkConditionsForStock(isin);
	}

	private initStatesAndCheckConditions(isin: string) {
		this.stockStates[isin] = {
			LastTradedPrice: undefined,
			LastTradedPriceVarPercent: undefined,
			TotalNumberOfSharesTraded: undefined,
			BestBuyLimitQuantity_1: undefined,
			BestSellLimitQuantity_1: undefined,
			ClosingPrice: undefined,
			ClosingPriceVarPercent: undefined,
			BestBuyLimitPrice_1: undefined,
			BestSellLimitPrice_1: undefined,
			TotalTradeValue: undefined,
			FeeOfPreviousDaysClosingPrice: undefined
		};
		this.subscribeToStockOnLsConnection(isin);
	}

	private initStatesAndCheckConditionsForAll(isins: string[]) {
		for (const isin of isins) {
			this.stockStates[isin] = {
				LastTradedPrice: undefined,
				LastTradedPriceVarPercent: undefined,
				TotalNumberOfSharesTraded: undefined,
				BestBuyLimitQuantity_1: undefined,
				BestSellLimitQuantity_1: undefined,
				ClosingPrice: undefined,
				ClosingPriceVarPercent: undefined,
				BestBuyLimitPrice_1: undefined,
				BestSellLimitPrice_1: undefined,
				TotalTradeValue: undefined,
				FeeOfPreviousDaysClosingPrice: undefined
			};
		}
		this.subscribeToAllStocskOnLsConnection(isins);
	}

	private subscribeToStockOnLsConnection(isin: string) {
		this.subscribeToStock(isin);
		// this.subs.push(sub);
	}

	private subscribeToAllStocskOnLsConnection(isins: string[]) {
		this.subscribeToAllStocks(isins);
	}

	private subscribeToStock(isin: string) {
		const sub = this.lsService.subscribeToLs(LsSchemaType.Conditional, isin).subscribe(data => {
			this.fillMarketLastTradeInfo(data, isin);
			this.checkConditionsForStock(isin);
		});
		this.stockSubs[isin] = sub;
		this.subscribeToMarketData(isin);
	}

	private fillMarketLastTradeInfo(data: ILsData, isin: string) {
		switch (data.name) {
			case LsSchemas.ConditionalSchema.LastTradedPrice:
				this.stockStates[isin].LastTradedPrice = +data.val;
				break;
			case LsSchemas.ConditionalSchema.lastTradedPriceVar:
				this.stockStates[isin].LastTradedPriceVarPercent = +data.val;
				break;
			case LsSchemas.ConditionalSchema.FeeOfPreviousDaysClosingPrice:
				this.stockStates[isin].FeeOfPreviousDaysClosingPrice = +data.val;
				this.stockStates[isin].ClosingPriceVarPercent = this.symbolService.calculateClosingPriceVarPercent(
					this.stockStates[isin].ClosingPrice,
					this.stockStates[isin].FeeOfPreviousDaysClosingPrice,
					this.stockStates[isin].TotalNumberOfSharesTraded
				);
				break;

			case LsSchemas.ConditionalSchema.ClosingPrice:
				this.stockStates[isin].ClosingPrice = +data.val;
				this.stockStates[isin].ClosingPriceVarPercent = this.symbolService.calculateClosingPriceVarPercent(
					this.stockStates[isin].ClosingPrice,
					this.stockStates[isin].FeeOfPreviousDaysClosingPrice,
					this.stockStates[isin].TotalNumberOfSharesTraded
				);
				break;

			case LsSchemas.ConditionalSchema.TotalNumberOfSharesTraded:
				this.stockStates[isin].TotalNumberOfSharesTraded = +data.val;
				this.stockStates[isin].ClosingPriceVarPercent = this.symbolService.calculateClosingPriceVarPercent(
					this.stockStates[isin].ClosingPrice,
					this.stockStates[isin].FeeOfPreviousDaysClosingPrice,
					this.stockStates[isin].TotalNumberOfSharesTraded
				);

				break;
			case LsSchemas.ConditionalSchema.BestBuyLimitPrice_1:
				this.stockStates[isin].BestSellLimitPrice_1 = +data.val;
				break;
			case LsSchemas.ConditionalSchema.BestBuyLimitQuantity_1:
				this.stockStates[isin].BestSellLimitQuantity_1 = +data.val;
				break;
			case LsSchemas.ConditionalSchema.BestSellLimitPrice_1:
				this.stockStates[isin].BestSellLimitPrice_1 = +data.val;
				break;
			case LsSchemas.ConditionalSchema.BestSellLimitQuantity_1:
				this.stockStates[isin].BestSellLimitQuantity_1 = +data.val;
				break;
			case LsSchemas.ConditionalSchema.TotalTradeValue:
				this.stockStates[isin].TotalTradeValue = +data.val;
				break;
		}
	}

	private subscribeToAllStocks(isins: string[]) {
		for (const isin of isins) {
			const sub = this.lsService.subscribeToLs(LsSchemaType.Conditional, isin).subscribe(data => {
				this.fillMarketLastTradeInfo(data, isin);
				this.checkConditionsForStock(isin);
			});
			this.stockSubs[isin] = sub;
		}
		// this.subscribeAllToMarketData(isins);
	}

	private unsubscribeStock(isin: string) {
		const sub = this.stockSubs[isin];
		if (sub) {
			sub.unsubscribe();
			this.stockSubs[isin] = undefined;
		}
		this.lsService.unsubscribeSchema(LsSchemaType.Conditional, isin);
	}

	private subscribeToMarketData(isin: string) {
		this.marketDataService
			.GetStockListConditionData([isin])
			.pipe(
				take(1),
				retryWhen(error$ =>
					error$.pipe(
						delay(ConditionalNotificationConstants.DelayBetweenRetryGetStockData),
						scan(count => (count += 1), 1),
						tap(retryCnt => {
							if (retryCnt > ConditionalNotificationConstants.MaxRetryForGettingStockData) {
								throw 'subscribeToMarketData error';
							}
						})
					)
				)
			)
			.subscribe({
				next: stockData => {
					this.stockStates[isin].LastTradedPrice = stockData[0].lastTradedPrice;
					this.stockStates[isin].LastTradedPriceVarPercent = stockData[0].priceVar;

					this.stockStates[isin].TotalNumberOfSharesTraded = stockData[0].totalNumberOfSharesTraded;
					this.stockStates[isin].BestBuyLimitQuantity_1 = stockData[0].bestBuyQuantity;
					this.stockStates[isin].BestSellLimitQuantity_1 = stockData[0].bestSellQuantity;
					this.stockStates[isin].ClosingPrice = stockData[0].closingPrice;
					this.stockStates[isin].ClosingPriceVarPercent = this.symbolService.calculateClosingPriceVarPercent(
						stockData[0].lastTradedPrice,
						stockData[0].feeOfPreviousDaysClosingPrice,
						stockData[0].totalNumberOfSharesTraded
					);

					this.stockStates[isin].BestBuyLimitPrice_1 = stockData[0].bestBuyPrice;
					this.stockStates[isin].BestSellLimitPrice_1 = stockData[0].bestSellPrice;
					this.stockStates[isin].TotalTradeValue = stockData[0].totalTradeValue;

					this.checkConditionsForStock(isin);
				}
			});
	}

	private subscribeAllToMarketData(isins: string[]) {
		this.marketDataService
			.GetStockListConditionData(isins)
			.pipe(
				take(1),
				retryWhen(error$ =>
					error$.pipe(
						delay(ConditionalNotificationConstants.DelayBetweenRetryGetStockListData),
						scan(count => (count += 1), 1),
						tap(retryCnt => {
							if (retryCnt > ConditionalNotificationConstants.MaxRetryForGettingStockListData) {
								throw 'subscribeAllToMarketData error';
							}
						})
					)
				)
			)
			.subscribe({
				next: stockListData => {
					for (const isin of isins) {
						const stockData = stockListData.find(d => d.symbolISIN.toLowerCase() === isin.toLowerCase());
						if (!stockData) continue;
						this.stockStates[isin].LastTradedPrice = stockData.lastTradedPrice;
						this.stockStates[isin].LastTradedPriceVarPercent = stockData.priceVar;
						this.stockStates[isin].TotalNumberOfSharesTraded = stockData.totalNumberOfSharesTraded;
						this.stockStates[isin].BestBuyLimitQuantity_1 = stockData.bestBuyQuantity;
						this.stockStates[isin].BestSellLimitQuantity_1 = stockData.bestSellQuantity;
						this.stockStates[isin].ClosingPrice = stockData.closingPrice;
						this.stockStates[isin].ClosingPriceVarPercent =
							this.symbolService.calculateClosingPriceVarPercent(
								stockData.lastTradedPrice,
								stockData.feeOfPreviousDaysClosingPrice,
								stockData.totalNumberOfSharesTraded
							);

						this.stockStates[isin].BestBuyLimitPrice_1 = stockData.bestBuyPrice;
						this.stockStates[isin].BestSellLimitPrice_1 = stockData.bestSellPrice;
						this.stockStates[isin].TotalTradeValue = stockData.totalTradeValue;
						this.checkConditionsForStock(isin);
					}
				}
			});
	}

	private checkConditionsForStock(isin: string) {
		// checking current time and avoid stock-condition-check out of Market time
		if (!this.timeService.isMarketOpenSub.value) return;

		const thisStockState = this.stockStates[isin];
		if (!thisStockState) return;
		const fieldValuesForStockState = this.getFiledValuesForStockState(thisStockState);
		const relatedStockConditions = this.pendingConditions.filter(c => c.stockIsin === isin);
		relatedStockConditions.forEach(condition => {
			if (condition.isTrue(fieldValuesForStockState)) {
				const symbolName = this.stockIsinMapper[isin.toUpperCase()]?.symbol;
				// let stockTitle = this.stockIsinMapper[isin].title;
				const notifTitle = condition.name
					? `شرط ${condition.name} از نماد ${symbolName} برقرار شد`
					: `شرط   ${symbolName}  برقرار شد`;

				const notifBody = condition.name ? condition.name : this.getStockConditionDescription(condition);
				if (this.userDeviceService.deviceType !== DeviceTypeEnum.Mobile) {
					this.notificationService.alert(
						notifTitle,
						NotificationType.info,
						false,
						this.getDeviceEndRootPath(),
						isin
					);
				} else {
					this.notificationService.alert(
						notifTitle,
						NotificationType.info,
						false,
						this.getDeviceEndRootPath() + `/${isin}`
					);
				}

				this.notificationService.notif({
					title: notifTitle,
					body: notifBody,
					defaultUrl: this.getDeviceEndRootPath() + `/${isin}`
				});
				this.notifSubject.next(new Date());
				this.playAudio();
				this.vibrate();
				this.addNotifiedConditionId(condition.id);
				// condition.isNotified = true;
				this.unsubscribeStockIfNeeded(isin);
				this.emitUnseenNotifiedConditionsCount();
			}
		});
	}

	vibrate() {
		try {
			if (this.userDeviceService.deviceType != DeviceTypeEnum.Mobile) return;
			if (this.userDeviceService.isiOS) return;
			const canVibrate = navigator.vibrate;
			if (!canVibrate) return;
			navigator.vibrate([
				ConditionalNotificationConstants.ShortVibrateDuration,
				ConditionalNotificationConstants.SilentDuration,
				ConditionalNotificationConstants.LongVibrateDuration
			]);
		} catch (e) {
			null;
		}
	}

	getDeviceEndRootPath = () =>
		`${
			this.userDeviceService.deviceType == DeviceTypeEnum.Mobile
				? '/menu/' + SharedRoutes.CONDITION_ROUTE
				: '/' + SharedRoutes.CONDITION_ROUTE
		}`;

	private unsubscribeStockIfNeeded(isin: string) {
		if (!this.pendingIsinsSet.has(isin)) this.unsubscribeStock(isin);
	}

	private getFiledValuesForStockState(stockState: IStockState): IFieldValue[] {
		return [
			{ field: Field.LastTradedPrice, value: stockState.LastTradedPrice },
			{
				field: Field.LastTradedPriceVarPercent,
				value: stockState.LastTradedPriceVarPercent
			},
			{
				field: Field.TotalNumberOfSharesTraded,
				value: stockState.TotalNumberOfSharesTraded
			},
			{
				field: Field.BestBuyLimitQuantity_1,
				value: stockState.BestBuyLimitQuantity_1
			},
			{
				field: Field.BestSellLimitQuantity_1,
				value: stockState.BestSellLimitQuantity_1
			},
			{ field: Field.ClosingPrice, value: stockState.ClosingPrice },
			{
				field: Field.ClosingPriceVarPercent,
				value: stockState.ClosingPriceVarPercent
			},
			{
				field: Field.BestBuyLimitPrice_1,
				value: stockState.BestBuyLimitPrice_1
			},
			{
				field: Field.BestSellLimitPrice_1,
				value: stockState.BestSellLimitPrice_1
			},
			{ field: Field.TotalTradeValue, value: stockState.TotalTradeValue }
		];
	}

	getSimpleConditionDescription(simpleCondition: SimpleCondition): string {
		let text = '';
		text += new FieldType(simpleCondition.fieldValue.field).persianName;
		text += ' ';
		text += new ConditionOperatorType(simpleCondition.operator).persianName;
		text += ' ';
		text += this.decimalPipe.transform(simpleCondition.fieldValue.value);
		return text;
	}

	getSimpleConditionDescriptionHtml(simpleCondition: SimpleCondition): string {
		let text = '';
		text += new FieldType(simpleCondition.fieldValue.field).persianName;
		text += '&nbsp;';
		text += new ConditionOperatorType(simpleCondition.operator).persianName;
		text += "&nbsp;<span dir='ltr'>&nbsp;";
		text += this.decimalPipe.transform(simpleCondition.fieldValue.value);
		text += '</span>';
		return text;
	}

	getTotalSimpleConditionsDescription(simpleConditions: SimpleCondition[]) {
		let text = '';
		simpleConditions.forEach(simpleCond => {
			if (text.length > 0) text += ' و ';
			text += this.getSimpleConditionDescription(simpleCond);
		});
		return text;
	}

	getTotalSimpleConditionsDescriptionHtml(simpleConditions: SimpleCondition[]) {
		let text = '';
		simpleConditions.forEach(simpleCond => {
			if (text.length > 0) text += ' و ';
			text += this.getSimpleConditionDescriptionHtml(simpleCond);
		});
		return text;
	}

	getStockConditionDescription(stockCondition: StockCondition): string {
		return this.getTotalSimpleConditionsDescription(stockCondition.simpleConditions);
	}

	getStockConditionDescriptionHtml(stockCondition: StockCondition): string {
		return this.getTotalSimpleConditionsDescriptionHtml(stockCondition.simpleConditions);
	}

	flagNotifiedConditionAsSeen(stockCondition: StockCondition) {
		stockCondition.notifiedConditionIsSeen = true;
		this.updateStockCondition(stockCondition, false);
		this.emitUnseenNotifiedConditionsCount();
	}

	pendingOrNotifiedUnseenConditionExistsForStock(isin: string): boolean {
		const uscs = this.userStockConditionSets;
		if (!uscs || !isin) return false;
		const stockConditionSet = uscs.stockConditionSets.find(x => x.isin.toUpperCase() === isin.toUpperCase());
		if (!stockConditionSet) return false;
		return stockConditionSet.stockConditionItems.some(
			c => this.isConditionNotifiedAndUnseen(c) || !this.isConditionNotified(c)
		);
	}

	notifiedUnseenConditionExistsForStock(isin: string): boolean {
		const uscs = this.userStockConditionSets;
		if (!uscs) return false;
		const stockConditionSet = uscs.stockConditionSets.find(x => x.isin.toUpperCase() === isin.toUpperCase());
		if (!stockConditionSet) return false;
		return stockConditionSet.stockConditionItems.some(c => this.isConditionNotifiedAndUnseen(c));
	}

	getStockConditionFigureClass(isin: string): string {
		if (this.pendingOrNotifiedUnseenConditionExistsForStock(isin)) {
			if (this.notifiedUnseenConditionExistsForStock(isin)) return 'mdi-bell-ring text-warning';
			return 'mdi-bell-outline text-warning';
		}
		return 'mdi-bell-plus-outline';
	}

	getStockConditionIconClassesInConditionsPage(stockCondition: StockCondition): string {
		const conditionState = this.getStockConditionState(stockCondition);
		switch (conditionState) {
			case StockConditionState.NotifiedUnseen:
				return 'mdi-bell-ring text-warning mdi-rotate-45 bellshake';
			case StockConditionState.NotifiedSeen:
				return 'mdi-bell text-muted';
			case StockConditionState.Waiting:
				return 'mdi-timer-sand text-global';
			default:
				return '';
		}
	}

	getStockConditionStateDescription(stockCondition: StockCondition): string {
		const conditionState = this.getStockConditionState(stockCondition);
		switch (conditionState) {
			case StockConditionState.NotifiedUnseen:
				return 'هشدار برقرار شده';
			case StockConditionState.NotifiedSeen:
				return 'هشدار خاموش شده';
			case StockConditionState.Waiting:
				return 'هشدار در انتظار';
			default:
				return '';
		}
	}

	getStockConditionState(stockCondition: StockCondition): StockConditionState {
		if (this.isConditionNotified(stockCondition)) {
			if (!stockCondition.notifiedConditionIsSeen) return StockConditionState.NotifiedUnseen;
			return StockConditionState.NotifiedSeen;
		}
		return StockConditionState.Waiting;
	}

	isConditionNotified(stockCondition: StockCondition): boolean {
		return this.notifiedConditionIds.includes(stockCondition.id);
	}

	isConditionNotifiedAndUnseen(stockCondition: StockCondition): boolean {
		return this.isConditionNotified(stockCondition) && !stockCondition.notifiedConditionIsSeen;
	}

	addNotifiedConditionId(id: string) {
		const _notifiedConditionIds = this.notifiedConditionIds;
		_notifiedConditionIds.push(id);
		localStorage.setItem(this.notifiedConditionIdsStorageKey, JSON.stringify(_notifiedConditionIds));
	}

	removeNotifiedConditionId(id: string) {
		const _notifiedConditionIds = this.notifiedConditionIds.filter(x => x !== id);
		localStorage.setItem(this.notifiedConditionIdsStorageKey, JSON.stringify(_notifiedConditionIds));
	}

	getSortedStockConditions(isin: string): StockCondition[] {
		if (isin && this.userStockConditionSets) {
			const activeStockConditionSets = this.userStockConditionSets.stockConditionSets.find(
				x => x.isin.toUpperCase() == isin.toUpperCase()
			);
			if (activeStockConditionSets) {
				const stockConditionItems = activeStockConditionSets.stockConditionItems;
				const sortedStockConditionItems = pSortGenericByOrderType<StockCondition>(
					stockConditionItems,
					'creationDateTime',
					'dsc'
				);
				return sortedStockConditionItems;
			}
		}
		return [];
	}

	getSortedStockConditionSetWithSymbols(): StockConditionSetWithSymbol[] {
		if (!this.userStockConditionSets) return [];
		const stockConditionSetWithSymbols: StockConditionSetWithSymbol[] = [];
		const conditionSet = this.userStockConditionSets.stockConditionSets;
		for (let i = 0; i < conditionSet.length; i++) {
			const condItem = conditionSet[i];
			const isin = condItem.isin.toUpperCase();
			if (!this.stockIsinMapper[isin]) continue;
			stockConditionSetWithSymbols.push({
				...condItem,
				symbol: this.stockIsinMapper[isin]?.symbol,
				title: this.stockIsinMapper[isin]?.title
			});
		}
		const sortedSymbolList = pSortGeneric<StockConditionSetWithSymbol>(stockConditionSetWithSymbols, 'symbol');
		return sortedSymbolList;
	}

	getNotifiedUnseenConditionsCount(stockConditionItems: StockCondition[]): number {
		return stockConditionItems.filter(condition => this.isConditionNotifiedAndUnseen(condition)).length;
	}

	getPendingConditionsCount(stockConditionItems: StockCondition[]): number {
		return stockConditionItems.filter(condition => !this.isConditionNotified(condition)).length;
	}

	enableAccessibility() {
		if (this.userDeviceService.isiOS)
			this.notificationService.alert(
				'در حال حاضر امکان استفاده از هشدار درون برنامه‌ای در ios وجود ندارد.',
				NotificationType.warning
			);

		return !this.userDeviceService.isiOS;
	}

	hasAlarm$(isin) {
		return this.stockConditionSetCountObs$.pipe(
			map(alarms => {
				return alarms.get(isin) ?? null;
			})
		);
	}
}

enum StockConditionState {
	Waiting,
	NotifiedUnseen,
	NotifiedSeen
}
